import * as React from "react";
import type { 
  StatePerformanceTableProps, 
  StateData, 
  GeographicTableColumn, 
  TableSortConfig, 
  TableFilterConfig 
} from '../../types/geographic';

// Table columns configuration
const COLUMNS: GeographicTableColumn[] = [
  { key: 'rank_by_users', label: 'Rank', sortable: true, width: '80px' },
  { key: 'state_name', label: 'State', sortable: true },
  { key: 'state_code', label: 'Code', sortable: true, width: '80px' },
  { key: 'user_count', label: 'Total Users', sortable: true, format: 'number' },
  { key: 'client_count', label: 'Clients', sortable: true, format: 'number' },
  { key: 'professional_count', label: 'Professionals', sortable: true, format: 'number' },
  { key: 'revenue', label: 'Revenue', sortable: true, format: 'currency' },
  { key: 'roi', label: 'ROI', sortable: true, format: 'percentage' },
  { key: 'population', label: 'Population', sortable: true, format: 'number' }
];

// Utility functions
const formatValue = (value: any, format?: GeographicTableColumn['format']): string => {
  if (value == null) return '-';
  
  switch (format) {
    case 'number':
      return typeof value === 'number' ? value.toLocaleString() : value.toString();
    case 'currency':
      if (typeof value === 'number') {
        return new Intl.NumberFormat('en-US', { 
          style: 'currency', 
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(value);
      } else {
        return '-';
      }
    case 'percentage':
      if (typeof value === 'number') {
        return `${value.toFixed(1)}%`;
      } else {
        return '-';
      }
    default:
      return value.toString();
  }
};

const sortData = (data: StateData[], sortConfig: TableSortConfig): StateData[] => {
  return [...data].sort((a, b) => {
    const aValue = (a as any)[sortConfig.column];
    const bValue = (b as any)[sortConfig.column];
    
    // Handle null/undefined values
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return 1;
    if (bValue == null) return -1;
    
    // Sort based on type
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    }
    
    // String comparison
    const aStr = aValue.toString().toLowerCase();
    const bStr = bValue.toString().toLowerCase();
    
    if (sortConfig.direction === 'asc') {
      return aStr.localeCompare(bStr);
    } else {
      return bStr.localeCompare(aStr);
    }
  });
};

const filterData = (data: StateData[], filters: TableFilterConfig): StateData[] => {
  return data.filter(state => {
    // Search term filter
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      const searchableText = `${state.state_name} ${state.state_code}`.toLowerCase();
      if (!searchableText.includes(searchLower)) {
        return false;
      }
    }
    
    // Min user count filter
    if (filters.minUserCount && state.user_count < filters.minUserCount) {
      return false;
    }
    
    // States filter
    if (filters.states?.length && !filters.states.includes(state.state_code)) {
      return false;
    }
    
    return true;
  });
};

// Table header component
const TableHeader: React.FC<{
  column: GeographicTableColumn;
  sortConfig: TableSortConfig | null;
  onSort: (column: string, direction: 'asc' | 'desc') => void;
}> = ({ column, sortConfig, onSort }) => {
  const isSorted = sortConfig?.column === column.key;
  const currentDirection = isSorted ? sortConfig!.direction : null;
  
  const handleSort = () => {
    if (!column.sortable) return;
    
    let newDirection: 'asc' | 'desc' = 'asc';
    if (isSorted && currentDirection === 'asc') {
      newDirection = 'desc';
    }
    
    onSort(column.key, newDirection);
  };

  return (
    <th
      style={{ width: column.width }}
      className={`${column.sortable ? 'cursor-pointer user-select-none' : ''}`}
      onClick={handleSort}
    >
      <div className="d-flex align-items-center justify-content-between">
        <span>{column.label}</span>
        {column.sortable && (
          <div className="ms-1">
            {isSorted ? (
              <i className={`fas fa-sort-${currentDirection === 'asc' ? 'up' : 'down'} text-primary`} aria-hidden="true"></i>
            ) : (
              <i className="fas fa-sort text-muted" aria-hidden="true"></i>
            )}
          </div>
        )}
      </div>
    </th>
  );
};

// Table row component
const TableRow: React.FC<{
  state: StateData;
  columns: GeographicTableColumn[];
  onClick?: (state: StateData) => void;
  index: number;
}> = React.memo(({ state, columns, onClick, index }) => {
  const handleClick = () => {
    if (onClick) {
      onClick(state);
    }
  };

  // Add computed rank if not present
  const stateWithRank = {
    ...state,
    rank_by_users: state.rank_by_users || index + 1
  };

  return (
    <tr 
      className={`${onClick ? 'cursor-pointer table-row-hover' : ''}`}
      onClick={handleClick}
    >
      {columns.map(column => (
        <td key={column.key}>
          {column.key === 'state_name' ? (
            <div className="d-flex align-items-center">
              <div>
                <div className="fw-semibold">{state.state_name}</div>
                {state.population && (
                  <small className="text-muted">
                    Pop: {state.population.toLocaleString()}
                  </small>
                )}
              </div>
            </div>
          ) : (
            <span className={column.format === 'number' ? 'font-monospace' : ''}>
              {formatValue((stateWithRank as any)[column.key], column.format)}
            </span>
          )}
        </td>
      ))}
    </tr>
  );
});

TableRow.displayName = 'TableRow';

// Pagination component
const Pagination: React.FC<{
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  pageSize: number;
  totalItems: number;
}> = ({ currentPage, totalPages, onPageChange, pageSize, totalItems }) => {
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);
  
  if (totalPages <= 1) return null;

  return (
    <div className="d-flex justify-content-between align-items-center">
      <div className="text-muted small">
        Showing {startItem}-{endItem} of {totalItems} states
      </div>
      <nav aria-label="Table pagination">
        <ul className="pagination pagination-sm mb-0">
          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
            <button
              className="page-link"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <i className="fas fa-chevron-left" aria-hidden="true"></i>
            </button>
          </li>
          
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum: number;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }
            
            return (
              <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => onPageChange(pageNum)}
                >
                  {pageNum}
                </button>
              </li>
            );
          })}
          
          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
            <button
              className="page-link"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              <i className="fas fa-chevron-right" aria-hidden="true"></i>
            </button>
          </li>
        </ul>
      </nav>
    </div>
  );
};

/**
 * StatePerformanceTable Component
 * 
 * A comprehensive data table for displaying state-level performance metrics
 * with sorting, filtering, pagination, and interactive features.
 * 
 * Features:
 * - Sortable columns with visual indicators
 * - Real-time search filtering
 * - Pagination with customizable page size
 * - Click handlers for drill-down functionality
 * - Loading states with skeleton rows
 * - Responsive design with horizontal scrolling
 * - Number formatting with proper locale support
 * - ROI and revenue display
 * 
 * Usage:
 * ```tsx
 * <StatePerformanceTable
 *   data={statesData}
 *   onSort={(column, direction) => handleSort(column, direction)}
 *   onFilter={(filters) => handleFilter(filters)}
 *   onRowClick={(state) => drillDownToState(state)}
 *   pageSize={25}
 * />
 * ```
 */
const StatePerformanceTable: React.FC<StatePerformanceTableProps> = ({
  data,
  isLoading = false,
  onSort,
  onFilter,
  onRowClick,
  pageSize = 25,
  className = ""
}) => {
  // Local state for table functionality
  const [sortConfig, setSortConfig] = React.useState<TableSortConfig | null>({
    column: 'user_count',
    direction: 'desc'
  });
  const [filters, setFilters] = React.useState<TableFilterConfig>({
    searchTerm: '',
    minUserCount: undefined,
    states: undefined
  });
  const [currentPage, setCurrentPage] = React.useState(1);

  // Process data with sorting and filtering
  const processedData = React.useMemo(() => {
    let result = filterData(data, filters);
    if (sortConfig) {
      result = sortData(result, sortConfig);
    }
    return result;
  }, [data, filters, sortConfig]);

  // Pagination calculations
  const totalPages = Math.ceil(processedData.length / pageSize);
  const paginatedData = React.useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return processedData.slice(startIndex, startIndex + pageSize);
  }, [processedData, currentPage, pageSize]);

  // Handle sorting
  const handleSort = React.useCallback((column: string, direction: 'asc' | 'desc') => {
    const newSortConfig = { column, direction };
    setSortConfig(newSortConfig);
    setCurrentPage(1); // Reset to first page when sorting
    
    if (onSort) {
      onSort(column, direction);
    }
  }, [onSort]);

  // Handle search
  const handleSearchChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = event.target.value;
    const newFilters = { ...filters, searchTerm };
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filtering
    
    if (onFilter) {
      onFilter(newFilters);
    }
  }, [filters, onFilter]);

  // Handle min user count filter
  const handleMinUserCountChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const minUserCount = value ? parseInt(value) : undefined;
    const newFilters = { ...filters, minUserCount };
    setFilters(newFilters);
    setCurrentPage(1);
    
    if (onFilter) {
      onFilter(newFilters);
    }
  }, [filters, onFilter]);

  // Reset filters
  const handleResetFilters = React.useCallback(() => {
    const resetFilters = { searchTerm: '', minUserCount: undefined, states: undefined };
    setFilters(resetFilters);
    setCurrentPage(1);
    
    if (onFilter) {
      onFilter(resetFilters);
    }
  }, [onFilter]);

  // Loading skeleton
  const LoadingSkeleton = () => (
    <tbody>
      {Array.from({ length: pageSize }, (_, index) => (
        <tr key={index}>
          {COLUMNS.map(column => (
            <td key={column.key}>
              <div className="placeholder-glow">
                <div className="placeholder col-8" style={{ height: '1rem' }}></div>
              </div>
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );

  return (
    <div className={`state-performance-table ${className}`.trim()}>
      {/* Filters */}
      <div className="row mb-3">
        <div className="col-md-6">
          <div className="input-group">
            <span className="input-group-text">
              <i className="fas fa-search" aria-hidden="true"></i>
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search states..."
              value={filters.searchTerm}
              onChange={handleSearchChange}
            />
          </div>
        </div>
        <div className="col-md-3">
          <div className="input-group">
            <span className="input-group-text">
              <i className="fas fa-users" aria-hidden="true"></i>
            </span>
            <input
              type="number"
              className="form-control"
              placeholder="Min users"
              value={filters.minUserCount || ''}
              onChange={handleMinUserCountChange}
              min="0"
            />
          </div>
        </div>
        <div className="col-md-3">
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={handleResetFilters}
              disabled={!filters.searchTerm && !filters.minUserCount}
            >
              <i className="fas fa-times me-1" aria-hidden="true"></i>
              Clear
            </button>
            <div className="text-muted small align-self-center">
              {processedData.length} states
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table className="table table-hover table-striped">
          <thead className="table-dark">
            <tr>
              {COLUMNS.map(column => (
                <TableHeader
                  key={column.key}
                  column={column}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
              ))}
            </tr>
          </thead>
          
          {isLoading ? (
            <LoadingSkeleton />
          ) : processedData.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={COLUMNS.length} className="text-center py-4 text-muted">
                  <i className="fas fa-search fa-2x mb-2" aria-hidden="true"></i>
                  <p className="mb-0">
                    {data.length === 0 
                      ? "No state data available" 
                      : "No states match your search criteria"
                    }
                  </p>
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {paginatedData.map((state, index) => (
                <TableRow
                  key={state.state_code}
                  state={state}
                  columns={COLUMNS}
                  onClick={onRowClick}
                  index={(currentPage - 1) * pageSize + index}
                />
              ))}
            </tbody>
          )}
        </table>
      </div>

      {/* Pagination */}
      {!isLoading && processedData.length > 0 && (
        <div className="mt-3">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            pageSize={pageSize}
            totalItems={processedData.length}
          />
        </div>
      )}

      {/* Custom CSS for hover effects */}
      <style>{`
        .cursor-pointer {
          cursor: pointer !important;
        }
        
        .table-row-hover:hover {
          background-color: rgba(13, 110, 253, 0.1) !important;
        }
        
        .user-select-none {
          user-select: none;
        }
        
        th.cursor-pointer:hover {
          background-color: rgba(255, 255, 255, 0.1);
        }
      `}</style>
    </div>
  );
};

StatePerformanceTable.displayName = 'StatePerformanceTable';

export default StatePerformanceTable;